/**
 * Meeting Notes Constants
 *
 * Shared constants for meeting notes provider mappings.
 * These constants ensure consistent display labels and API paths across the application.
 */

/* eslint-disable react-refresh/only-export-components -- shared constants file also exports the MeetingNoteAttribution component used by all three meeting-note surfaces */
import { memo } from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { cx } from '@/utils/cx';

/**
 * Maps meeting notes source provider values to their display labels.
 *
 * Used for showing user-friendly provider names in timeline events and meeting notes tabs.
 *
 * @example
 * ```typescript
 * const label = MEETING_NOTES_SOURCE_LABELS['google_meet']; // 'Google Meet'
 * ```
 */
export const MEETING_NOTES_SOURCE_LABELS: Record<string, string> = {
  google_meet: 'Google Meet',
  granola: 'Granola',
  zoom: 'Zoom',
  fathom: 'Fathom',
  recall_bot: 'Ahoy Notetaker',
  recall_sdk: 'Ahoy Notetaker',
  fireflies: 'Fireflies',
};

/**
 * Maps meeting notes source provider values to their API path segments.
 *
 * Used for constructing transcript API endpoints based on the provider.
 * Only includes providers that expose a separate transcript endpoint.
 * Providers not in this mapping store transcripts directly in CRM properties.
 *
 * @example
 * ```typescript
 * const path = MEETING_NOTES_PROVIDER_PATHS['zoom']; // 'meetings'
 * const url = `/api/orgs/${orgId}/${path}/notes/${entityId}/transcript/`;
 * ```
 */
export const MEETING_NOTES_PROVIDER_PATHS: Record<string, string> = {
  zoom: 'meetings',
  google_meet: 'meetings',
  granola: 'meetings',
  // Fathom stores transcripts in CRM properties, not via separate endpoint
};

/**
 * Association name linking a meeting note to the integration that created it.
 */
export const MEETING_NOTES_TO_INTEGRATION_ASSOCIATION =
  'ahoy_meeting_notes_to_integration';

/**
 * Association name linking a meeting note directly to its owning user.
 */
export const MEETING_NOTES_TO_OWNER_ASSOCIATION = 'ahoy_meeting_notes_to_owner';

/** Text/icon size classes per surface — keeps each surface's existing sizing exact. */
const SIZE_VARIANTS: Record<
  'tab' | 'card' | 'fullpage',
  { text: string; icon: string }
> = {
  tab: { text: 'text-xs', icon: 'size-3.5' },
  card: { text: 'text-xs', icon: 'size-4' },
  fullpage: { text: 'text-sm', icon: 'size-5' },
};

interface MeetingNoteAttributionProps {
  /** Provider label (e.g. "Granola"). Omit to render only the owner badge. */
  sourceLabel?: string;
  /** Provider icon URL, shown beside sourceLabel when both are present. */
  sourceIcon?: string;
  /** Owner entity to render as a badge. Omit to render only the source label. */
  ownerEntityId?: string;
  orgId: number;
  /** Forwarded to EntityBadgeById — every call site must state this explicitly. */
  interactive: boolean;
  /** Resolves text/icon sizing via SIZE_VARIANTS — MeetingNotesTab='tab', MeetingSummaryCard='card', MeetingSummaryFullPage='fullpage'. */
  variant: 'tab' | 'card' | 'fullpage';
  /** When true, show the middot whenever ownerEntityId is present, regardless of sourceLabel. When false, only when both are present. */
  alwaysShowSeparator: boolean;
}

/**
 * Shared provider-label + middot-separator + owner-badge composition used by
 * MeetingNotesTab, MeetingSummaryCard, and MeetingSummaryFullPage.
 */
export const MeetingNoteAttribution = memo(function MeetingNoteAttribution({
  sourceLabel,
  sourceIcon,
  ownerEntityId,
  orgId,
  interactive,
  variant,
  alwaysShowSeparator,
}: MeetingNoteAttributionProps) {
  const { text: textClass, icon: iconClass } = SIZE_VARIANTS[variant];
  const showSeparator =
    !!ownerEntityId && (alwaysShowSeparator || !!sourceLabel);

  return (
    <>
      {sourceLabel && (
        <>
          {sourceIcon && (
            <img
              src={sourceIcon}
              alt={sourceLabel}
              className={cx(iconClass, 'object-contain')}
            />
          )}
          <span className={cx(textClass, 'text-fg-secondary')}>
            {sourceLabel}
          </span>
        </>
      )}
      {showSeparator && (
        <span aria-hidden='true' className={cx(textClass, 'text-fg-tertiary')}>
          ·
        </span>
      )}
      {ownerEntityId && (
        <EntityBadgeById
          entityId={ownerEntityId}
          orgId={orgId}
          variant='xs'
          interactive={interactive}
        />
      )}
    </>
  );
});
